import { Controller } from '@hotwired/stimulus'

const POLL_INTERVAL = 3000
const GIVE_UP_AFTER = 60000

// The bulletin is generated asynchronously right after the amount step. While it is missing the
// investor sees a waiting screen, reloaded until the signature call to action appears. Doclift can fail
// for good (missing template, provider outage) and the job only reports to Appsignal, so the wait is
// bounded: past GIVE_UP_AFTER the reloading stops and an error with the support contact is revealed.
export default class extends Controller {
  static targets = ['error']
  static values = { ready: Boolean, key: String }

  connect() {
    if (this.readyValue) {
      this.#clearDeadline()
      return
    }

    if (this.#elapsed() > GIVE_UP_AFTER) {
      this.#giveUp()
      return
    }

    this.timer = setTimeout(() => window.location.reload(), POLL_INTERVAL)
  }

  disconnect() {
    clearTimeout(this.timer)
  }

  // Wall-clock across reloads: the page is reloaded whole, so the deadline cannot live in memory.
  #elapsed() {
    const started = this.#read()
    if (started === null) {
      this.#write(Date.now())
      return 0
    }

    return Date.now() - started
  }

  #giveUp() {
    this.#clearDeadline()
    if (this.hasErrorTarget) this.errorTarget.classList.remove('d-none')
  }

  get #storageKey() {
    return `contract-polling:${this.keyValue}`
  }

  #read() {
    try {
      const raw = window.sessionStorage.getItem(this.#storageKey)
      return raw === null ? null : Number(raw)
    } catch {
      return null
    }
  }

  #write(value) {
    try {
      window.sessionStorage.setItem(this.#storageKey, String(value))
    } catch {
      // Private browsing or blocked storage: fall back to polling without a deadline.
    }
  }

  #clearDeadline() {
    try {
      window.sessionStorage.removeItem(this.#storageKey)
    } catch {
      // Nothing to clean up when storage is unavailable.
    }
  }
}
